/**
 * CryoVault Frozen Storage — Dropdown Constants & Types
 *
 * All enum/dropdown options used in the AddFrozenStorageModal form.
 */

import { parseGardnerGrade } from "../cryovault/embryoGrade";

// ============================================================================
// Shared
// ============================================================================

export type DropdownOption = { id: string; label: string };

export const STORAGE_LOCATION_TYPES: DropdownOption[] = [
  { id: "clinic", label: "Clinic" },
  { id: "hospital", label: "Hospital" },
  { id: "cryobank", label: "Cryobank" },
  { id: "other", label: "Other" },
];

export const COUNTRIES: DropdownOption[] = [
  { id: "US", label: "United States" },
  { id: "GB", label: "United Kingdom" },
  { id: "CA", label: "Canada" },
  { id: "AU", label: "Australia" },
  { id: "DE", label: "Germany" },
  { id: "FR", label: "France" },
  { id: "ES", label: "Spain" },
  { id: "IT", label: "Italy" },
  { id: "CZ", label: "Czech Republic" },
  { id: "GR", label: "Greece" },
  { id: "CY", label: "Cyprus" },
  { id: "IN", label: "India" },
  { id: "JP", label: "Japan" },
  { id: "MX", label: "Mexico" },
  { id: "IL", label: "Israel" },
  { id: "OTHER", label: "Other" },
];

/** Common fertility clinics for searchable dropdown */
export const COMMON_CLINICS: string[] = [
  "CCRM Fertility",
  "Shady Grove Fertility",
  "RMA of New York",
  "Boston IVF",
  "NYU Langone Fertility Center",
  "UCSF Center for Reproductive Health",
  "Spring Fertility",
  "Kindbody",
  "Columbia University Fertility Center",
  "Weill Cornell Reproductive Medicine",
  "CNY Fertility",
  "SGF (Shady Grove Fertility)",
  "Mate Fertility",
  "Prelude Fertility",
  "Extend Fertility",
  "Igenomix",
  "CREATE Fertility (UK)",
  "ARGC (UK)",
  "Care Fertility (UK)",
  "Genea (Australia)",
  "IVF Australia",
];

// ============================================================================
// Oocyte-specific
// ============================================================================

export const FREEZING_METHODS: DropdownOption[] = [
  { id: "vitrification", label: "Vitrification" },
  { id: "slow_freeze", label: "Slow Freeze" },
  { id: "unknown", label: "Unknown" },
];

export const OOCYTE_STATUSES: DropdownOption[] = [
  { id: "in_storage", label: "In Storage" },
  { id: "partially_thawed", label: "Partially Thawed" },
  { id: "all_thawed", label: "All Thawed" },
  { id: "used", label: "Used" },
  { id: "discarded", label: "Discarded" },
  { id: "donated", label: "Donated" },
];

// ============================================================================
// Embryo-specific
// ============================================================================

export const EMBRYO_DAY_OPTIONS: DropdownOption[] = [
  { id: "1", label: "Day 1" },
  { id: "2", label: "Day 2" },
  { id: "3", label: "Day 3 (Cleavage)" },
  { id: "4", label: "Day 4 (Morula)" },
  { id: "5", label: "Day 5 (Blastocyst)" },
  { id: "6", label: "Day 6 (Blastocyst)" },
  { id: "7", label: "Day 7" },
];

export const EMBRYO_GRADES: DropdownOption[] = [
  { id: "6AA", label: "6AA – Excellent" },
  { id: "5AA", label: "5AA – Excellent" },
  { id: "4AA", label: "4AA – Excellent" },
  { id: "4AB", label: "4AB – Very Good" },
  { id: "4BA", label: "4BA – Very Good" },
  { id: "4BB", label: "4BB – Good" },
  { id: "3AA", label: "3AA – Good" },
  { id: "3AB", label: "3AB – Good" },
  { id: "3BA", label: "3BA – Fair" },
  { id: "3BB", label: "3BB – Fair" },
  { id: "2BB", label: "2BB – Fair" },
  { id: "other", label: "Other / Unknown" },
];

export const FERTILISATION_METHODS: DropdownOption[] = [
  { id: "ivf", label: "Conventional IVF" },
  { id: "icsi", label: "ICSI" },
  { id: "split", label: "Split IVF/ICSI" },
  { id: "unknown", label: "Unknown" },
];

export const PGT_OPTIONS: DropdownOption[] = [
  { id: "not_performed", label: "Not Performed" },
  { id: "pgt_a", label: "PGT-A (Aneuploidy)" },
  { id: "pgt_m", label: "PGT-M (Monogenic)" },
  { id: "pgt_sr", label: "PGT-SR (Structural)" },
  { id: "pgt_a_m", label: "PGT-A + PGT-M" },
  { id: "pgt_p", label: "PGT-P (Polygenic)" },
  { id: "pending", label: "Pending Results" },
];

export const GENETIC_STATUSES: DropdownOption[] = [
  { id: "euploid", label: "Euploid (Normal)" },
  { id: "aneuploid", label: "Aneuploid (Abnormal)" },
  { id: "mosaic_low", label: "Low-level Mosaic" },
  { id: "mosaic_high", label: "High-level Mosaic" },
  { id: "no_result", label: "No Result" },
  { id: "not_tested", label: "Not Tested" },
  { id: "pending", label: "Pending" },
];

export const EMBRYO_STATUSES: DropdownOption[] = [
  { id: "frozen", label: "Frozen" },
  { id: "arrested", label: "Arrested / not frozen" },
  { id: "transferred", label: "Transferred" },
  { id: "discarded", label: "Discarded" },
  { id: "donated", label: "Donated" },
  { id: "research", label: "Donated to Research" },
  { id: "thawed_refrozen", label: "Thawed & Refrozen" },
];

export const SEX_OPTIONS: DropdownOption[] = [
  { id: "unknown", label: "Unknown" },
  { id: "xx", label: "XX (Female)" },
  { id: "xy", label: "XY (Male)" },
  { id: "prefer_not", label: "Prefer Not to Know" },
];

// ============================================================================
// Types — Cycle Records
// ============================================================================

export type SpecimenFormType = "eggs" | "embryos" | "sperm";
export type SpecimenSource = "mine" | "partner" | "donor" | "unknown";
export type StorageState = "stored" | "not_stored" | "unknown";

/** Patient-facing preparation methods for stored semen specimens. */
export const SEMEN_PROCESSING_METHODS: DropdownOption[] = [
  { id: "none", label: "No additional preparation" },
  { id: "washed", label: "Washed" },
  { id: "swim_up", label: "Swim-up selection" },
  { id: "density_gradient", label: "Density-gradient selection" },
  { id: "dgc_swim_up", label: "Density-gradient + swim-up" },
  { id: "microfluidics", label: "Microfluidic selection" },
  { id: "macs", label: "MACS selection" },
  { id: "other", label: "Other / I don't know" },
];

/** Collection methods supported by the API for semen specimens. */
export const SEMEN_COLLECTION_METHODS: DropdownOption[] = [
  { id: "ejaculation", label: "Ejaculation" },
  { id: "tese", label: "TESE (testicular extraction)" },
  { id: "pesa", label: "PESA (epididymal aspiration)" },
  { id: "mesa", label: "MESA (microsurgical extraction)" },
  { id: "other", label: "Other" },
];

/** File attachment metadata */
export type FileAttachment = {
  name: string;
  uri: string;
  mimeType?: string;
  uploadId?: string;
  documentId?: string;
};

/** Oocyte-specific fields */
export type OocyteCohortDisposition =
  "all_frozen" | "mixed_frozen_fertilized" | "all_fertilized" | "unknown";

/** The four retrieval counts, each of which she may take back. */
export type OocyteCountMetric =
  "totalRetrieved" | "matureMII" | "immatureMI" | "degenerated";

/**
 * Where each of those counts is written: the step that owns it, and the field
 * it becomes.
 *
 * The step matters as much as the field, and getting it wrong is not inert:
 * both steps map onto the same `egg_retrieval` phase, so a count routed to the
 * wrong one still reaches the rows and erases the wrong figure. Stating the two
 * together here beats sorting them out at the call site, where "everything
 * except mature" was one added count away from doing exactly that.
 */
export const OOCYTE_COUNT_TARGET: Record<
  OocyteCountMetric,
  { step: "egg_retrieval" | "freezing"; field: string }
> = {
  totalRetrieved: { step: "egg_retrieval", field: "eggsCollected" },
  // The freezing tab shares the retrieval phase but owns this one.
  matureMII: { step: "freezing", field: "matureEggs" },
  immatureMI: { step: "egg_retrieval", field: "immatureMI" },
  degenerated: { step: "egg_retrieval", field: "degenerated" },
};

export type OocyteFields = {
  retrievalDate: string;
  freezeDate: string;
  freezingMethod: string;
  totalRetrieved: number | null;
  matureMII: number | null;
  immatureMI: number | null;
  degenerated: number | null;
  numberFrozen: number;
  cohortDisposition: OocyteCohortDisposition | null;
  numberFertilized: number | null;
  currentStatus: string;
  numberThawed: number | null;
  postThawSurvivalRate: number | null;
};

/** Embryo individual entry row */
export type EmbryoIndividualEntry = {
  id: string;
  embryoId: string;
  /** Furthest developmental day reached by this embryo. */
  dayOfDevelopment: string;
  grade: string;
  geneticStatus: string;
  sex: string;
  status: string;
  notes: string;
};

/** Embryo-specific fields */
export type EmbryoFields = {
  fertilisationDate: string;
  freezeDate: string;
  dayOfDevelopment: string;
  fertilisationMethod: string;
  pgtPerformed: string;
  /** Group mode fields */
  totalEmbryosCreated: number | null;
  numberFrozen: number;
  numberTested: number | null;
  numberEuploid: number | null;
  numberAneuploid: number | null;
  numberMosaic: number | null;
  numberNoResult: number | null;
  numberTransferred: number | null;
  numberDiscarded: number | null;
  currentlyInStorage: number | null;
  storageOverride: boolean;
  /** Individual mode entries */
  individualEntries: EmbryoIndividualEntry[];
};

export type EmbryoEntryMode = "group" | "individual";

/** Sperm-specific fields (used when type === "sperm") */
export type SemenFields = {
  collectedAt: string;
  freezeDate: string;
  numberFrozen: number;
  /** Maps to API processingMethod. */
  processingMethod: string;
  /** Maps to API collectionMethod. */
  collectionMethod: string;
  /** Days of abstinence before collection. */
  abstinencePeriod: number | null;
  origin: "mine" | "partner" | "donor" | "unknown";
  isDonor: boolean;
  // optional — when populated, they're written to the API spermiogram
  concentration: number | null;
  totalCount: number | null;
  motilityPercent: number | null;
  morphologyPercent: number | null;
  volumeMl: number | null;
  dnaFragmentationPercent: number | null;
  vitalityPercent: number | null;
  progressiveMotilityPercent: number | null;
  otherDetails: string;
};

/**
 * What a saved row already holds, so an update names it instead of duplicating
 * it.
 *
 * The intake writes the whole record on every save, so it may only touch a row
 * it reopened faithful to — that fidelity is `parseNotesString` plus the
 * invariant in `notesRoundTrip.test.ts`. What is named here is what the server
 * reconciles rather than creates: the storage episode, and each recorded event
 * with the record nested inside it.
 *
 * Lives here, with `Cycle`, rather than beside the reader that builds it:
 * `specimenIntakePrefill` and `specimenIntakeUpdate` both already import this
 * module, and putting it in either would close an import cycle.
 */
export type SavedSpecimenRef = {
  /**
   * Its open frozen placement — the episode an update must name and a reduction
   * closes with `op: "delete"`.
   *
   * An update that carries a placement without naming the one it means asks for
   * a second, and `createStorageEpisode` refuses a further placement while an
   * unresolved one is open. A patient's free-text facility never resolves to a
   * site entity, so her episodes are always the unresolved kind: unnamed
   * collides every time.
   */
  storageLocationId?: string;
  /**
   * Its recorded events, by kind, so an update reconciles them instead of
   * appending. A usage sent unnamed is created, so three reopens would leave
   * three freezes in her clinical record.
   *
   * `detailId` is the event's nested record — the freezing info, the thawing
   * info, the biopsy — and naming the usage without naming it is worse than
   * naming neither. `handleFreezingInfo` branches on the nested id alone, so an
   * unnamed one takes the insert path against a `specimen_usage_id` that is
   * `notNull().unique()`: a raw constraint violation, which the batch reports
   * as `internal_error` because it is not an `ApiError`. That is what the first
   * browser test hit.
   */
  usageIds?: Partial<
    Record<"freeze" | "thaw" | "biopsy", { id: string; detailId?: string }>
  >;
};

/** Full cycle record (one card in the form) */
export type Cycle = {
  id: string;
  type: SpecimenFormType;
  /** The material source is explicit; null means it has not been reviewed. */
  source: SpecimenSource | null;
  clinicName: string;
  country: string;
  /** Human-readable city, region, or country selected for storage. */
  storageCity: string;
  /** Country name returned by Places for display when the country is outside the legacy list. */
  storageCountryName: string;
  /** Storage is a reviewed state, not something inferred from a count. */
  storageState: StorageState | null;
  storageLocationType: string;
  storageId: string;
  notes: string;
  files: FileAttachment[];
  /** Oocyte fields (used when type === "eggs") */
  oocyte: OocyteFields;
  /** Embryo fields (used when type === "embryos") */
  embryo: EmbryoFields;
  embryoEntryMode: EmbryoEntryMode;
  /** Sperm fields (used when type === "sperm") */
  sperm: SemenFields;
  /**
   * The rows this form was filled from — only those the intake may act on.
   *
   * A row she has thawed, used, reserved or discarded is left out entirely: the
   * write loop pairs `specimenIds[i]` with the row it writes, so listing one
   * would let a plain save write it back `stored`. The count reopens as the
   * length of this list, which is what the vault shows, so the indices agree
   * with the rows and nothing beyond them is touched.
   */
  specimenIds?: string[];
  /**
   * The counts she has explicitly marked unknown.
   *
   * Not a null value: `createEmptyCycle` defaults all four oocyte counts to
   * null and `handleAddAnotherSample` leaves the prefill unapplied, so a form
   * nobody touched is indistinguishable from a deliberate clear — writing nulls
   * blindly was tried and reverted in `bfde19e30` after it erased figures she
   * had already recorded. Taking a count back needs its own signal, and it has
   * to live on the cycle rather than in the counts sub-component: local state
   * dies on remount and never reaches `buildSavedPrefill`, which is the only
   * road from this form to the cycle's metric rows.
   */
  clearedOocyteCounts?: OocyteCountMetric[];
  /**
   * Which of `specimenIds` a reduction may retire, and what to close on them.
   * One entry per row here, since only reachable rows are listed at all.
   */
  specimenRefs?: Record<string, SavedSpecimenRef>;
  specimenIdsType?: SpecimenFormType;
};

// ============================================================================
// Factory helpers
// ============================================================================

export function createEmptyEmbryoEntry(): EmbryoIndividualEntry {
  return {
    id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
    embryoId: "",
    dayOfDevelopment: "",
    grade: "",
    geneticStatus: "",
    sex: "",
    status: "",
    notes: "",
  };
}

export function createEmptyCycle(): Cycle {
  return {
    id: Date.now().toString(),
    type: "eggs",
    source: null,
    clinicName: "",
    country: "",
    storageCity: "",
    storageCountryName: "",
    storageState: null,
    storageLocationType: "",
    storageId: "",
    notes: "",
    files: [],
    oocyte: {
      retrievalDate: "",
      freezeDate: "",
      freezingMethod: "",
      totalRetrieved: null,
      matureMII: null,
      immatureMI: null,
      degenerated: null,
      numberFrozen: 0,
      cohortDisposition: null,
      numberFertilized: null,
      currentStatus: "",
      numberThawed: null,
      postThawSurvivalRate: null,
    },
    embryo: {
      fertilisationDate: "",
      freezeDate: "",
      dayOfDevelopment: "",
      fertilisationMethod: "",
      pgtPerformed: "",
      totalEmbryosCreated: null,
      numberFrozen: 0,
      numberTested: null,
      numberEuploid: null,
      numberAneuploid: null,
      numberMosaic: null,
      numberNoResult: null,
      numberTransferred: null,
      numberDiscarded: null,
      currentlyInStorage: null,
      storageOverride: false,
      individualEntries: [],
    },
    embryoEntryMode: "group",
    sperm: {
      collectedAt: "",
      freezeDate: "",
      numberFrozen: 0,
      processingMethod: "",
      collectionMethod: "",
      abstinencePeriod: null,
      origin: "unknown",
      isDonor: false,
      concentration: null,
      totalCount: null,
      motilityPercent: null,
      morphologyPercent: null,
      volumeMl: null,
      dnaFragmentationPercent: null,
      vitalityPercent: null,
      progressiveMotilityPercent: null,
      otherDetails: "",
    },
  };
}

// ============================================================================
// Notes builder — serialize rich fields into notes for API
// ============================================================================

function labelFor(options: DropdownOption[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

export function buildNotesString(cycle: Cycle): string {
  const lines: string[] = [];

  // Shared header
  if (cycle.clinicName) lines.push(`Clinic: ${cycle.clinicName}`);
  if (cycle.country) {
    lines.push(`Country: ${labelFor(COUNTRIES, cycle.country)}`);
  }
  if (cycle.storageCity) {
    lines.push(`Storage Location: ${cycle.storageCity}`);
  }
  if (cycle.storageLocationType) {
    lines.push(
      `Storage Type: ${labelFor(STORAGE_LOCATION_TYPES, cycle.storageLocationType)}`,
    );
  }
  if (cycle.storageId) lines.push(`Storage ID: ${cycle.storageId}`);
  if (cycle.source) lines.push(`Specimen Source: ${cycle.source}`);
  if (cycle.storageState) lines.push(`Storage State: ${cycle.storageState}`);

  if (cycle.type === "eggs") {
    const o = cycle.oocyte;
    if (o.retrievalDate) {
      lines.push(`Retrieval Date: ${o.retrievalDate}`);
    }
    if (o.freezeDate) lines.push(`Freeze Date: ${o.freezeDate}`);
    if (o.freezingMethod) {
      lines.push(
        `Freezing Method: ${labelFor(FREEZING_METHODS, o.freezingMethod)}`,
      );
    }
    if (o.totalRetrieved != null) {
      lines.push(`Total Retrieved: ${o.totalRetrieved}`);
    }
    if (o.matureMII != null) lines.push(`Mature (MII): ${o.matureMII}`);
    if (o.immatureMI != null) lines.push(`Immature (MI): ${o.immatureMI}`);
    if (o.degenerated != null) lines.push(`Degenerated: ${o.degenerated}`);
    lines.push(`Number Frozen: ${o.numberFrozen}`);
    if (o.cohortDisposition) {
      lines.push(`Cohort Disposition: ${o.cohortDisposition}`);
    }
    if (o.numberFertilized != null) {
      lines.push(`Number Fertilized: ${o.numberFertilized}`);
    }
    if (o.numberThawed != null) lines.push(`Number Thawed: ${o.numberThawed}`);
    if (o.postThawSurvivalRate != null) {
      lines.push(`Post-Thaw Survival Rate: ${o.postThawSurvivalRate}%`);
    }
  } else if (cycle.type === "sperm") {
    const s = cycle.sperm;
    if (s.collectedAt) lines.push(`Collection Date: ${s.collectedAt}`);
    if (s.freezeDate) lines.push(`Freeze Date: ${s.freezeDate}`);
    if (s.collectionMethod) {
      lines.push(
        `Collection Method: ${labelFor(SEMEN_COLLECTION_METHODS, s.collectionMethod)}`,
      );
    }
    if (s.processingMethod) {
      lines.push(
        `Preparation Method: ${labelFor(SEMEN_PROCESSING_METHODS, s.processingMethod)}`,
      );
    }
    lines.push(`Vials Frozen: ${s.numberFrozen}`);
    if (s.abstinencePeriod != null) {
      lines.push(`Days of Abstinence: ${s.abstinencePeriod}`);
    }
    if (s.concentration != null) {
      lines.push(`Concentration: ${s.concentration} M/mL`);
    }
    if (s.totalCount != null) lines.push(`Total Count: ${s.totalCount} M`);
    if (s.motilityPercent != null) {
      lines.push(`Motility: ${s.motilityPercent}%`);
    }
    if (s.morphologyPercent != null) {
      lines.push(`Normal Morphology: ${s.morphologyPercent}%`);
    }
    if (s.volumeMl != null) lines.push(`Volume: ${s.volumeMl} mL`);
    if (s.dnaFragmentationPercent != null) {
      lines.push(`DNA Fragmentation: ${s.dnaFragmentationPercent}%`);
    }
    if (s.vitalityPercent != null) {
      lines.push(`Vitality: ${s.vitalityPercent}%`);
    }
    if (s.progressiveMotilityPercent != null) {
      lines.push(`Progressive Motility: ${s.progressiveMotilityPercent}%`);
    }
    if (s.otherDetails.trim()) {
      lines.push(`Other Sample Details: ${s.otherDetails.trim()}`);
    }
  } else {
    const e = cycle.embryo;
    if (e.fertilisationDate) {
      lines.push(`Fertilisation Date: ${e.fertilisationDate}`);
    }
    if (e.freezeDate) lines.push(`Freeze Date: ${e.freezeDate}`);
    if (e.fertilisationMethod) {
      lines.push(
        `Fertilisation: ${labelFor(FERTILISATION_METHODS, e.fertilisationMethod)}`,
      );
    }
    if (e.pgtPerformed) {
      lines.push(`PGT: ${labelFor(PGT_OPTIONS, e.pgtPerformed)}`);
    }

    if (cycle.embryoEntryMode === "group") {
      lines.push(
        `Day of Development: ${labelFor(EMBRYO_DAY_OPTIONS, e.dayOfDevelopment)}`,
      );
      if (e.totalEmbryosCreated != null) {
        lines.push(`Total Created: ${e.totalEmbryosCreated}`);
      }
      lines.push(`Number Frozen: ${e.numberFrozen}`);
      if (e.numberTested != null) {
        lines.push(`Number Tested: ${e.numberTested}`);
      }
      if (e.numberEuploid != null) {
        lines.push(`Euploid: ${e.numberEuploid}`);
      }
      if (e.numberAneuploid != null) {
        lines.push(`Aneuploid: ${e.numberAneuploid}`);
      }
      if (e.numberMosaic != null) {
        lines.push(`Mosaic: ${e.numberMosaic}`);
      }
      if (e.numberNoResult != null) {
        lines.push(`No Result: ${e.numberNoResult}`);
      }
    } else {
      lines.push(`--- Individual Embryos ---`);
      e.individualEntries.forEach((entry, i) => {
        const parts: string[] = [`#${i + 1}`];
        if (entry.embryoId) parts.push(`ID: ${entry.embryoId}`);
        if (entry.dayOfDevelopment) {
          parts.push(
            `Development: ${labelFor(EMBRYO_DAY_OPTIONS, entry.dayOfDevelopment)}`,
          );
        }
        if (entry.grade) {
          parts.push(`Grade: ${labelFor(EMBRYO_GRADES, entry.grade)}`);
        }
        if (entry.geneticStatus && entry.geneticStatus !== "not_tested") {
          parts.push(
            `Genetic: ${labelFor(GENETIC_STATUSES, entry.geneticStatus)}`,
          );
        }
        if (entry.sex && entry.sex !== "unknown") {
          parts.push(`Sex: ${labelFor(SEX_OPTIONS, entry.sex)}`);
        }
        if (["frozen", "arrested", "thawed_refrozen"].includes(entry.status)) {
          parts.push(`Status: ${labelFor(EMBRYO_STATUSES, entry.status)}`);
        }
        if (entry.notes) parts.push(`Notes: ${entry.notes}`);
        lines.push(parts.join(" | "));
      });
    }
  }

  // File attachments
  const fileNames = cycle.files.map((f) => f.name).filter(Boolean);
  if (fileNames.length > 0) {
    lines.push(`Files: ${fileNames.join(", ")}`);
  }

  if (cycle.notes.trim()) {
    lines.push("");
    lines.push(`Notes: ${cycle.notes.trim()}`);
  }

  return lines.join("\n");
}

// ============================================================================
// Slim notes builder — only fields WITHOUT a backend equivalent
// ============================================================================

/**
 * Build a notes string containing **only** fields that have no proper
 * backend column. All structured data (dates, counts, freezing method,
 * PGT, grades, storage locations) is sent via dedicated API fields.
 */
export function buildSlimNotesString(cycle: Cycle): string {
  const lines: string[] = [];
  if (cycle.country) {
    lines.push(`Country: ${labelFor(COUNTRIES, cycle.country)}`);
  }
  if (cycle.storageCity) {
    lines.push(`Storage Location: ${cycle.storageCity}`);
  }
  if (cycle.storageLocationType) {
    lines.push(
      `Storage Type: ${labelFor(STORAGE_LOCATION_TYPES, cycle.storageLocationType)}`,
    );
  }

  if (cycle.type === "eggs") {
    const o = cycle.oocyte;
    if (o.totalRetrieved != null) {
      lines.push(`Total Retrieved: ${o.totalRetrieved}`);
    }
    if (o.matureMII != null) lines.push(`Mature (MII): ${o.matureMII}`);
    if (o.immatureMI != null) lines.push(`Immature (MI): ${o.immatureMI}`);
    if (o.degenerated != null) lines.push(`Degenerated: ${o.degenerated}`);
  } else if (cycle.type === "sperm") {
    const s = cycle.sperm;
    if (s.collectionMethod) {
      lines.push(
        `Collection Method: ${labelFor(SEMEN_COLLECTION_METHODS, s.collectionMethod)}`,
      );
    }
    if (s.processingMethod) {
      lines.push(
        `Preparation Method: ${labelFor(SEMEN_PROCESSING_METHODS, s.processingMethod)}`,
      );
    }
    if (s.abstinencePeriod != null) {
      lines.push(`Days of Abstinence: ${s.abstinencePeriod}`);
    }
  } else {
    const e = cycle.embryo;
    if (e.fertilisationMethod) {
      lines.push(
        `Fertilisation: ${labelFor(FERTILISATION_METHODS, e.fertilisationMethod)}`,
      );
    }
    if (cycle.embryoEntryMode === "group") {
      if (e.totalEmbryosCreated != null) {
        lines.push(`Total Created: ${e.totalEmbryosCreated}`);
      }
      if (e.numberTested != null) {
        lines.push(`Number Tested: ${e.numberTested}`);
      }
      if (e.numberEuploid != null) {
        lines.push(`Euploid: ${e.numberEuploid}`);
      }
      if (e.numberAneuploid != null) {
        lines.push(`Aneuploid: ${e.numberAneuploid}`);
      }
      if (e.numberMosaic != null) {
        lines.push(`Mosaic: ${e.numberMosaic}`);
      }
      if (e.numberNoResult != null) {
        lines.push(`No Result: ${e.numberNoResult}`);
      }
      if (e.numberTransferred != null) {
        lines.push(`Transferred: ${e.numberTransferred}`);
      }
      if (e.numberDiscarded != null) {
        lines.push(`Discarded: ${e.numberDiscarded}`);
      }
      if (e.currentlyInStorage != null) {
        lines.push(`Currently in Storage: ${e.currentlyInStorage}`);
      }
    }
  }

  if (cycle.notes.trim()) {
    lines.push("");
    lines.push(cycle.notes.trim());
  }
  return lines.join("\n");
}

// ============================================================================
// Storage location meta — JSON backup for location, country & facility type
// ============================================================================

/** Structured contents of `storageLocations[].notes`. */
export type StorageLocationMeta = {
  country?: string;
  countryName?: string;
  location?: string;
  facilityType?: string;
  /** Raw storage reference kept whole when `parseTankRef` structures nothing. */
  storageId?: string;
};

/**
 * Serialize storage location meta for `storageLocations[].notes`.
 * Returns null when there is nothing to store.
 */
export function serializeStorageLocationMeta(
  meta: StorageLocationMeta,
): string | null {
  const payload: Record<string, string> = {};
  if (meta.country) payload.country = meta.country;
  if (meta.location) payload.location = meta.location;
  if (meta.countryName) payload.countryName = meta.countryName;
  if (meta.facilityType) payload.facilityType = meta.facilityType;
  if (meta.storageId) payload.storageId = meta.storageId;
  if (Object.keys(payload).length === 0) return null;
  return JSON.stringify(payload);
}

/**
 * Build a JSON string containing location, country and facility type for
 * `storageLocations[].notes`. This is a structured backup so these
 * fields survive even if the human-readable notes string is edited.
 * `unparsedStorageId` carries the raw storage reference when it produced no
 * structured tank fields, so it shares the field instead of overwriting it.
 */
export function buildStorageLocationMeta(
  cycle: Cycle,
  unparsedStorageId?: string,
): string | null {
  return serializeStorageLocationMeta({
    country: cycle.country,
    location: cycle.storageCity,
    countryName: cycle.storageCountryName,
    facilityType: cycle.storageLocationType,
    storageId: unparsedStorageId,
  });
}

/**
 * Parse storage location meta JSON back into structured data.
 * Returns null for non-JSON or missing input.
 */
export function parseStorageLocationMeta(
  notes: string | null | undefined,
): StorageLocationMeta | null {
  if (!notes) return null;
  try {
    const parsed = JSON.parse(notes);
    if (typeof parsed === "object" && parsed !== null) {
      const result: StorageLocationMeta = {};
      if (typeof parsed.country === "string") result.country = parsed.country;
      if (typeof parsed.countryName === "string") {
        result.countryName = parsed.countryName;
      }
      if (typeof parsed.location === "string") {
        result.location = parsed.location;
      }
      if (typeof parsed.facilityType === "string") {
        result.facilityType = parsed.facilityType;
      }
      if (typeof parsed.storageId === "string") {
        result.storageId = parsed.storageId;
      }
      return Object.keys(result).length > 0 ? result : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Build the `storageLocations[]` entry for a frozen cycle. Structured tank
 * fields and the meta JSON both land in the payload: an unparsed storage
 * reference travels inside the meta rather than replacing it.
 */
export function buildFrozenStorageLocation(
  cycle: Cycle,
  storedFrom: string,
  facility: string,
) {
  const { notes: unparsedStorageId, ...tankFields } = parseTankRef(
    cycle.storageId,
  );
  return {
    storedFrom,
    isActive: true,
    // Omitted rather than sent empty. The server keeps this as
    // `locator.legacy.facilityText` and reads it back as-is, so an empty string
    // would come back as a facility whose name is "" — which every caller then
    // has to tell apart from an absent one. Absent says it plainly.
    ...(facility.trim() ? { facility: facility.trim() } : {}),
    storageType: "frozen" as const,
    ...tankFields,
    notes: buildStorageLocationMeta(cycle, unparsedStorageId),
  };
}

// ============================================================================
// API mapping helpers
// ============================================================================

/** Map the form's PGT dropdown + genetic status to API embryoBiopsy shape. */
export function buildEmbryoBiopsy(
  pgtPerformed: string,
  dayOfDevelopment: string,
  geneticStatus?: string,
): Record<string, unknown> | null {
  if (pgtPerformed === "not_performed" && !geneticStatus) return null;

  const biopsy: Record<string, unknown> = {};

  // Biopsy stage from day of development
  const dayNum = parseInt(dayOfDevelopment, 10);
  if (dayNum === 3) biopsy.biopsyStage = "day_3";
  else if (dayNum === 5) biopsy.biopsyStage = "day_5";
  else if (dayNum === 6) biopsy.biopsyStage = "day_6";

  // PGT type booleans. The current embryo API has no sentToPgtp field, so
  // PGT-P remains represented in the serialized notes until the API supports
  // a structured polygenic-testing field.
  if (pgtPerformed === "pgt_a") {
    biopsy.sentToPgta = true;
  } else if (pgtPerformed === "pgt_m") {
    biopsy.sentToPgtm = true;
  } else if (pgtPerformed === "pgt_sr") {
    biopsy.sentToPgtsr = true;
  } else if (pgtPerformed === "pgt_a_m") {
    biopsy.sentToPgta = true;
    biopsy.sentToPgtm = true;
  }

  // Genetic status → euploidy + mosaicism
  if (geneticStatus) {
    if (geneticStatus === "euploid") biopsy.euploidyStatus = "euploid";
    else if (geneticStatus === "aneuploid") biopsy.euploidyStatus = "aneuploid";
    else if (geneticStatus === "mosaic_low") {
      biopsy.hasMosaicism = true;
      biopsy.mosaicismLevel = "low_level";
    } else if (geneticStatus === "mosaic_high") {
      biopsy.hasMosaicism = true;
      biopsy.mosaicismLevel = "high_level";
    }
  }

  return Object.keys(biopsy).length > 0 ? biopsy : null;
}

/** Structured tank fields, plus the raw reference when nothing parsed. */
export type ParsedTankRef = {
  tankNumber?: string;
  canisterNumber?: string;
  caneNumber?: string;
  subcanisterNumber?: string;
  rackNumber?: string;
  strawNumber?: string;
  /** The whole storage reference, when no structured field could be read. */
  notes?: string;
};

/**
 * Best-effort parse of storage ID like "Tank 4, Canister B, Straw 12" into
 * structured storage-location fields.
 * Falls back to putting the whole string in `notes`.
 */
export function parseTankRef(storageId: string): ParsedTankRef {
  if (!storageId.trim()) return {};

  const result: ParsedTankRef = {};
  /*
   * Matched case-insensitively against the original rather than against a
   * lowercased copy, so a captured value keeps the case she typed. Reading it
   * off `lower` turned "Canister B" into "b", which nothing noticed while
   * nothing read these fields back — and now that the intake reopens on them,
   * a round trip would quietly rewrite her own reference in lower case.
   */
  const lower = storageId;

  const tankMatch = lower.match(/tank\s*[#:]?\s*(\w+)/i);
  if (tankMatch) result.tankNumber = tankMatch[1]!;

  const canisterMatch = lower.match(/\bcanister\s*[#:]?\s*(\w+)/i);
  if (canisterMatch) result.canisterNumber = canisterMatch[1]!;

  const caneMatch = lower.match(/cane\s*[#:]?\s*(\w+)/i);
  if (caneMatch) result.caneNumber = caneMatch[1]!;

  const subcanisterMatch = lower.match(/sub[-\s]?canister\s*[#:]?\s*(\w+)/i);
  if (subcanisterMatch) result.subcanisterNumber = subcanisterMatch[1]!;

  const rackMatch = lower.match(/rack\s*[#:]?\s*(\w+)/i);
  if (rackMatch) result.rackNumber = rackMatch[1]!;

  const strawMatch = lower.match(/straw\s*[#:]?\s*(\w+)/i);
  if (strawMatch) result.strawNumber = strawMatch[1]!;

  // If nothing was parsed, put the raw string into notes
  if (Object.keys(result).length === 0) {
    result.notes = storageId;
  }

  return result;
}

/**
 * Map form status to the API specimen status enum.
 *
 * Form values: "frozen"|"in_storage"|"partially_thawed"|"all_thawed"|
 *              "transferred"|"discarded"|"donated"|"used"|"research"|"thawed_refrozen"
 * API values:  "collected"|"processed"|"stored"|"used"|"discarded"|"donated"|"in_transit"
 */
export function mapEntryStatus(formStatus: string): string {
  switch (formStatus) {
    case "frozen":
    case "in_storage":
    case "partially_thawed":
    case "thawed_refrozen":
      return "stored";
    case "transferred":
    case "all_thawed":
    case "used":
      return "used";
    case "discarded":
    case "arrested":
      return "discarded";
    case "donated":
    case "research":
      return "donated";
    default:
      // An untouched/unknown form value must not create an active stored
      // record. Callers may promote it to `stored` only after storage is
      // explicitly confirmed and represented by a storage location.
      return "collected";
  }
}

/**
 * Parse an embryo grade string like "4AA" into dayDetails fields.
 * Returns `{ stage, gradingNumerical, icmGrade, teGrade }` for a Gardner
 * grade, on the Gardner scale: "4AA" is an expanded blastocyst.
 */
export function parseGrade(
  grade: string,
  dayOfDevelopment: string,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  const dayNum = parseInt(dayOfDevelopment, 10);
  if (!Number.isNaN(dayNum)) result.day = dayNum;

  if (!grade || grade === "other") return result;

  const gardner = parseGardnerGrade(grade);
  if (gardner) Object.assign(result, gardner);

  return result;
}

// ============================================================================
// Notes parser — deserialize notes back into structured data
// ============================================================================

/** Parsed data extracted from a notes string */
export type ParsedNotesData = {
  clinicName?: string;
  country?: string;
  storageCity?: string;
  storageLocationType?: string;
  storageId?: string;
  specimenSource?: SpecimenSource;
  storageState?: StorageState;
  freezingMethod?: string;
  freezeDate?: string;
  retrievalDate?: string;
  fertilisationDate?: string;
  dayOfDevelopment?: string;
  fertilisationMethod?: string;
  pgtPerformed?: string;
  totalRetrieved?: number;
  matureMII?: number;
  immatureMI?: number;
  degenerated?: number;
  numberFrozen?: number;
  /*
   * Written by the oocyte branch since it existed, never read — so both fell
   * into `freeTextNotes`, and once the intake started applying that back to the
   * form's own notes field, every reopen-and-save added another copy under
   * `Notes:`. `notesRoundTrip.test.ts` asserts write-read-write is stable now,
   * so a label added to the writer without a reader fails there.
   */
  cohortDisposition?: OocyteCohortDisposition;
  numberFertilized?: number;
  currentStatus?: string;
  numberThawed?: number;
  postThawSurvivalRate?: number;
  totalEmbryosCreated?: number;
  numberTested?: number;
  numberEuploid?: number;
  numberAneuploid?: number;
  numberMosaic?: number;
  numberNoResult?: number;
  numberTransferred?: number;
  numberDiscarded?: number;
  currentlyInStorage?: number;
  /*
   * Semen analysis, which the writer has always emitted and the parser never
   * read — so every one of these landed in `freeTextNotes` and the intake
   * reopened blank. They are here because the intake now has to reopen faithful
   * to what it wrote: it saves the whole record at once, so a field it cannot
   * read back is a field a save silently overwrites.
   */
  collectionDate?: string;
  collectionMethod?: string;
  processingMethod?: string;
  abstinencePeriod?: number;
  concentration?: number;
  totalCount?: number;
  motilityPercent?: number;
  morphologyPercent?: number;
  volumeMl?: number;
  dnaFragmentationPercent?: number;
  vitalityPercent?: number;
  progressiveMotilityPercent?: number;
  otherDetails?: string;
  freeTextNotes?: string;
  fileNames?: string[];
  individualEmbryos?: {
    embryoId?: string;
    dayOfDevelopment?: string;
    grade?: string;
    geneticStatus?: string;
    sex?: string;
    status?: string;
    notes?: string;
  }[];
};

function findIdByLabel(
  options: DropdownOption[],
  label: string,
): string | undefined {
  const match = options.find(
    (o) => o.label.toLowerCase() === label.toLowerCase(),
  );
  return match?.id;
}

function parseNumber(val: string): number | undefined {
  /*
   * `parseFloat`, not `parseInt`: the semen analysis and the post-thaw survival
   * rate are decimal fields in the form (`decimal-pad`, `Number.parseFloat`),
   * and truncating them was invisible only while this parse fed a read-only
   * view. The reopened intake writes what it reads, so `2.5 mL` came back as
   * `2 mL` and the save made the rounding permanent.
   *
   * Trailing units are written by `buildNotesString` ("40 M/mL", "55%") and
   * `parseFloat` stops at them, which is why they need no stripping here.
   */
  const n = Number.parseFloat(val);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Parse a notes string (produced by buildNotesString) back into structured data.
 * Gracefully handles missing or unrecognized lines.
 */
export function parseNotesString(notes: string): ParsedNotesData {
  const result: ParsedNotesData = {};
  const allLines = notes.split("\n");

  /*
   * Her own note runs to the end of the string.
   *
   * `buildNotesString` pushes it last, as a single `Notes: ` line whose value
   * may itself contain newlines — so splitting on "\n" scattered lines two
   * onward into the body, where they were dropped. Read-only that lost nothing
   * visible; now that the reopened form writes back what it read, it truncated
   * her note to its first line on every save. Everything from that marker on is
   * hers, verbatim.
   *
   * An individual embryo's own `Notes:` sits inside a `#`-prefixed, pipe-joined
   * line, so it can never be mistaken for this one.
   */
  const noteStart = allLines.findIndex((line) => {
    const trimmed = line.trim();
    return trimmed.startsWith("Notes:") && !trimmed.startsWith("#");
  });
  if (noteStart !== -1) {
    const own = allLines
      .slice(noteStart)
      .join("\n")
      .replace(/^\s*Notes:\s?/, "")
      .trim();
    if (own) result.freeTextNotes = own;
  }
  const lines = noteStart === -1 ? allLines : allLines.slice(0, noteStart);
  const freeTextLines: string[] = [];
  let inIndividualSection = false;
  const individualEmbryos: ParsedNotesData["individualEmbryos"] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (trimmed === "--- Individual Embryos ---") {
      inIndividualSection = true;
      continue;
    }

    if (inIndividualSection && trimmed.startsWith("#")) {
      // Parse individual embryo line: "#1 | ID: E1 | Grade: 4AA | ..."
      const parts = trimmed.split("|").map((p) => p.trim());
      const entry: NonNullable<ParsedNotesData["individualEmbryos"]>[number] =
        {};
      for (const part of parts) {
        if (part.startsWith("#")) continue;
        const [key, ...rest] = part.split(":");
        const val = rest.join(":").trim();
        if (!key || !val) continue;
        const k = key.trim().toLowerCase();
        if (k === "id") entry.embryoId = val;
        else if (k === "development") {
          entry.dayOfDevelopment =
            findIdByLabel(EMBRYO_DAY_OPTIONS, val) ?? val;
        } else if (k === "grade") {
          entry.grade = findIdByLabel(EMBRYO_GRADES, val) ?? val;
        } else if (k === "genetic") {
          entry.geneticStatus = findIdByLabel(GENETIC_STATUSES, val) ?? val;
        } else if (k === "sex") {
          entry.sex = findIdByLabel(SEX_OPTIONS, val) ?? val;
        } else if (k === "status") {
          entry.status = findIdByLabel(EMBRYO_STATUSES, val) ?? val;
        } else if (k === "notes") entry.notes = val;
      }
      individualEmbryos.push(entry);
      continue;
    }

    // Match "Key: Value" pattern
    const match = trimmed.match(/^([^:]+):\s*(.+)$/);
    if (!match) {
      freeTextLines.push(trimmed);
      continue;
    }

    const key = match[1]!.trim();
    const val = match[2]!.trim();

    switch (key) {
      case "Clinic":
        result.clinicName = val;
        break;
      case "Country":
        result.country = findIdByLabel(COUNTRIES, val) ?? val;
        break;
      case "Storage Location":
        result.storageCity = val;
        break;
      case "Storage Type":
        result.storageLocationType =
          findIdByLabel(STORAGE_LOCATION_TYPES, val) ?? val;
        break;
      case "Storage ID":
        result.storageId = val;
        break;
      case "Specimen Source":
        if (["mine", "partner", "donor", "unknown"].includes(val)) {
          result.specimenSource = val as SpecimenSource;
        }
        break;
      case "Storage State":
        if (["stored", "not_stored", "unknown"].includes(val)) {
          result.storageState = val as StorageState;
        }
        break;
      case "Retrieval Date":
        result.retrievalDate = val;
        break;
      case "Freeze Date":
        result.freezeDate = val;
        break;
      case "Freezing Method":
        result.freezingMethod = findIdByLabel(FREEZING_METHODS, val) ?? val;
        break;
      case "Total Retrieved":
        result.totalRetrieved = parseNumber(val);
        break;
      case "Mature (MII)":
        result.matureMII = parseNumber(val);
        break;
      case "Immature (MI)":
        result.immatureMI = parseNumber(val);
        break;
      case "Degenerated":
        result.degenerated = parseNumber(val);
        break;
      case "Number Frozen":
        result.numberFrozen = parseNumber(val);
        break;
      case "Status":
        result.currentStatus =
          findIdByLabel(OOCYTE_STATUSES, val) ??
          findIdByLabel(EMBRYO_STATUSES, val) ??
          val;
        break;
      case "Cohort Disposition":
        if (
          val === "all_frozen" ||
          val === "mixed_frozen_fertilized" ||
          val === "all_fertilized" ||
          val === "unknown"
        ) {
          result.cohortDisposition = val;
        }
        break;
      case "Number Fertilized":
        result.numberFertilized = parseNumber(val);
        break;
      case "Number Thawed":
        result.numberThawed = parseNumber(val);
        break;
      case "Collection Date":
        result.collectionDate = val;
        break;
      case "Collection Method":
        result.collectionMethod =
          findIdByLabel(SEMEN_COLLECTION_METHODS, val) ?? val;
        break;
      case "Preparation Method":
        result.processingMethod =
          findIdByLabel(SEMEN_PROCESSING_METHODS, val) ?? val;
        break;
      // The semen writer counts vials where the others count specimens.
      case "Vials Frozen":
        result.numberFrozen = parseNumber(val);
        break;
      case "Days of Abstinence":
        result.abstinencePeriod = parseNumber(val);
        break;
      // `parseNumber` reads the leading integer, so the unit the writer appends
      // ("40 M/mL", "55%") falls away on its own.
      case "Concentration":
        result.concentration = parseNumber(val);
        break;
      case "Total Count":
        result.totalCount = parseNumber(val);
        break;
      case "Motility":
        result.motilityPercent = parseNumber(val);
        break;
      case "Normal Morphology":
        result.morphologyPercent = parseNumber(val);
        break;
      case "Volume":
        result.volumeMl = parseNumber(val);
        break;
      case "DNA Fragmentation":
        result.dnaFragmentationPercent = parseNumber(val);
        break;
      case "Vitality":
        result.vitalityPercent = parseNumber(val);
        break;
      case "Progressive Motility":
        result.progressiveMotilityPercent = parseNumber(val);
        break;
      case "Other Sample Details":
        result.otherDetails = val;
        break;
      case "Post-Thaw Survival Rate":
        result.postThawSurvivalRate = parseNumber(val.replace("%", ""));
        break;
      case "Fertilisation Date":
        result.fertilisationDate = val;
        break;
      case "Day of Development":
        result.dayOfDevelopment = findIdByLabel(EMBRYO_DAY_OPTIONS, val) ?? val;
        break;
      case "Fertilisation":
        result.fertilisationMethod =
          findIdByLabel(FERTILISATION_METHODS, val) ?? val;
        break;
      case "PGT":
        result.pgtPerformed = findIdByLabel(PGT_OPTIONS, val) ?? val;
        break;
      case "Total Created":
        result.totalEmbryosCreated = parseNumber(val);
        break;
      case "Number Tested":
        result.numberTested = parseNumber(val);
        break;
      case "Euploid":
        result.numberEuploid = parseNumber(val);
        break;
      case "Aneuploid":
        result.numberAneuploid = parseNumber(val);
        break;
      case "Mosaic":
        result.numberMosaic = parseNumber(val);
        break;
      case "No Result":
        result.numberNoResult = parseNumber(val);
        break;
      case "Transferred":
        result.numberTransferred = parseNumber(val);
        break;
      case "Discarded":
        result.numberDiscarded = parseNumber(val);
        break;
      case "Currently in Storage":
        result.currentlyInStorage = parseNumber(val);
        break;

      case "Files":
        result.fileNames = val
          .split(",")
          .map((f) => f.trim())
          .filter(Boolean);
        break;
      default:
        freeTextLines.push(trimmed);
        break;
    }
  }

  if (individualEmbryos.length > 0) {
    result.individualEmbryos = individualEmbryos;
  }

  if (freeTextLines.length > 0 && !result.freeTextNotes) {
    result.freeTextNotes = freeTextLines.join("\n");
  }

  return result;
}
